/**
 * Cloud (platform) → Alert Config → Site offline: WHEN a site counts as offline, for every site.
 *
 * One default row ("All sites") that every site follows, and per site an optional setting of its
 * own. Who is told is the Site offline cards on Who gets notified (offline-routing on the backend).
 */
import { useEffect, useMemo, useState } from 'react';
import { SignalSlashIcon } from '@heroicons/react/24/outline';
import { AACard, AASwitch, BTN_PRIMARY_SM, BTN_SECONDARY_SM, Callout, alertActionsErrorText } from '@trinetra/shared';
import { getOfflineTriggers, saveOfflineTriggers, type OfflineTrigger, type OfflineTriggers } from '../services/api.service';

type SiteRow = OfflineTriggers['sites'][number];
const minutesOf = (s: number) => Math.max(1, Math.round(s / 60));
const numCls = 'w-20 rounded-lg border border-border bg-background px-2 py-1 text-[13px] text-foreground disabled:opacity-50';
const th = 'px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground';
const td = 'px-3 py-2 align-middle text-[13px] text-foreground';

function TriggerCells({ value, onChange, disabled }: { value: OfflineTrigger; onChange: (v: OfflineTrigger) => void; disabled: boolean }) {
  return (
    <>
      <td className={td}>
        <AASwitch checked={value.enabled} disabled={disabled} onChange={(enabled) => onChange({ ...value, enabled })} label="Alert when offline" />
      </td>
      <td className={td}>
        <span className="inline-flex items-center gap-1.5">
          <input
            type="number" min={1} max={1440} disabled={disabled || !value.enabled}
            value={minutesOf(value.after_seconds)}
            onChange={(e) => onChange({ ...value, after_seconds: Math.max(1, Number(e.target.value) || 1) * 60 })}
            className={numCls} aria-label="Offline after (minutes)"
          />
          <span className="text-muted-foreground">min</span>
        </span>
      </td>
      <td className={td}>
        <input type="checkbox" className="h-4 w-4 accent-brand" disabled={disabled || !value.enabled}
          checked={value.back_online} onChange={(e) => onChange({ ...value, back_online: e.target.checked })} aria-label="Tell when back online" />
      </td>
    </>
  );
}

export default function OfflineTriggersCard({ canEdit }: { canEdit: boolean }) {
  const [saved, setSaved] = useState<OfflineTriggers | null>(null);
  const [draft, setDraft] = useState<OfflineTriggers | null>(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    getOfflineTriggers().then((t) => { setSaved(t); setDraft(t); }).catch((e) => setError(alertActionsErrorText(e, 'Could not read the site offline settings')));
  }, []);

  const dirty = useMemo(() => JSON.stringify(saved) !== JSON.stringify(draft), [saved, draft]);
  const setSite = (nodeId: string, next: Partial<SiteRow>) => setDraft((d) => (d ? { ...d, sites: d.sites.map((s) => (s.node_id === nodeId ? { ...s, ...next } : s)) } : d));

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    setError('');
    try {
      const next = await saveOfflineTriggers({
        default: draft.default,
        sites: draft.sites.map(({ node_id, custom, enabled, after_seconds, back_online }) => ({ node_id, custom, enabled, after_seconds, back_online })),
      });
      setSaved(next);
      setDraft(next);
      setDone(true);
      setTimeout(() => setDone(false), 2500);
    } catch (e) {
      setError(alertActionsErrorText(e, 'Could not save'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <AACard
      icon={SignalSlashIcon}
      title="Site offline"
      subtitle="When a site stops reporting, the cloud raises an alert. Every site follows “All sites” unless it has its own. Who is told is set on Who gets notified."
      right={canEdit && draft ? (
        <div className="flex items-center gap-2">
          {done && <span className="text-[12px] text-success">Saved</span>}
          {dirty && <button type="button" onClick={() => setDraft(saved)} className={BTN_SECONDARY_SM}>Undo</button>}
          <button type="button" onClick={save} disabled={!dirty || saving} className={BTN_PRIMARY_SM}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      ) : null}
    >
      {error && <div className="mb-2"><Callout tone="danger">{error}</Callout></div>}
      {!draft ? (
        <p className="text-[13px] text-muted-foreground">{error ? '' : 'Loading…'}</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full min-w-[36rem]">
            <thead className="bg-muted">
              <tr><th className={th}>Site</th><th className={th}>Alert when offline</th><th className={th}>Offline after</th><th className={th}>Tell when back online</th><th className={th} /></tr>
            </thead>
            <tbody className="divide-y divide-border">
              <tr className="bg-card">
                <td className={`${td} font-semibold`}>All sites <span className="font-normal text-muted-foreground">(default)</span></td>
                <TriggerCells value={draft.default} disabled={!canEdit} onChange={(v) => setDraft({ ...draft, default: v })} />
                <td className={td} />
              </tr>
              {draft.sites.map((s) => {
                const shown = s.custom ? s : { ...s, ...draft.default };
                return (
                  <tr key={s.node_id}>
                    <td className={td}>{s.name}</td>
                    <TriggerCells value={shown} disabled={!canEdit || !s.custom} onChange={(v) => setSite(s.node_id, v)} />
                    <td className={`${td} whitespace-nowrap text-right`}>
                      {s.custom ? (
                        canEdit && <button type="button" onClick={() => setSite(s.node_id, { custom: false })} className="text-[12px] font-medium text-brand hover:underline">Use default</button>
                      ) : (
                        <>
                          <span className="mr-2 text-[12px] text-muted-foreground">Default</span>
                          {canEdit && <button type="button" onClick={() => setSite(s.node_id, { ...draft.default, custom: true })} className="text-[12px] font-medium text-brand hover:underline">Change</button>}
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
              {draft.sites.length === 0 && <tr><td className={`${td} text-muted-foreground`} colSpan={5}>No sites registered yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </AACard>
  );
}
